import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Flex,
  Heading,
  useMediaQuery,
  Skeleton,
  useDisclosure,
  useMergeRefs,
  FlexProps,
  Box
} from '@chakra-ui/react'
import { StyledTab } from 'styles'

import { FloatingButton, SearchLoader } from 'components'
import { generatePrices } from 'utils'
import {
  Enum_Propertycategory_Type,
  PropertyCategory,
  usePropertyCategoriesQuery
} from 'generated/graphql'

import { useBrowserStorage, useGeolocation } from 'hooks'
import { LAST_SELECTED_FILTERS_STORAGE_KEY } from '../../../constants'
import { SearchInitialValues } from 'types'
import { SearchBar } from '..'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { FiSearch } from 'react-icons/fi'
import { get } from 'js-cookie'
import CustomTooltip from 'components/CustomTooltip'

const SearchContainer = styled(Flex)`
  width: 100%;
  &::after {
    content: '';
    z-index: -4;
    position: absolute;
    top: 0;
    left: 50%;
    width: 100vw;
    transform: translate(-50%, 0);
  }
  &::before {
    content: '';
    z-index: -3;
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 100vw;
    height: 90%;
    transform: translate(-50%, 0);
    clip-path: polygon(31% 0, 100% 41%, 100% 100%, 0 78%);
  }
  @media (max-width: 40em) {
    &::after {
      border-radius: 0;
    }
  }
`
type SearchProps = {
  showDescription?: boolean
  isResult?: boolean
  shouldShowSearch?: boolean
} & FlexProps
// const icons = [VscHome, FaHome, FaBuilding, RiCommunityFill]

const Search = React.forwardRef<HTMLDivElement, SearchProps>(
  ({ showDescription, isResult, shouldShowSearch, ...rest }, ref) => {
    const [isTabletOrMobile] = useMediaQuery('(max-width: 40em)')
    const { data: propertyCategories, loading: propertyCategoriesLoading } =
      usePropertyCategoriesQuery({
        fetchPolicy: 'cache-first'
      })
    const { currency } = useGeolocation()
    const isRand = currency.currency === 'ZAR'
    const { t } = useTranslation()
    const [localLastSelectedFilters] = useBrowserStorage<
      SearchInitialValues & { tab: PropertyCategory }
    >(LAST_SELECTED_FILTERS_STORAGE_KEY, 'local')
    const categories = propertyCategories?.propertyCategories
    const [currentTerm, setCurrentTerm] = useState(() =>
      localLastSelectedFilters ? `${localLastSelectedFilters?.tab.type}_heading` : `rent_heading`
    )

    const searchContainer = useRef<HTMLDivElement>(null)
    const { isOpen, onOpen, onClose } = useDisclosure()

    useLayoutEffect(() => {
      const tracker = () => {
        const rec = searchContainer?.current?.getBoundingClientRect() as DOMRect

        if (rec?.top * -1 > rec?.height / 2) {
          onOpen()
        } else {
          onClose()
        }
      }
      document.addEventListener('scroll', tracker)
      return () => {
        document.removeEventListener('scroll', tracker)
      }
    }, [])

    const lng = get('lng')
    useEffect(() => {
      setCurrentTerm((prev) => {
        const [category] = prev.split('_')
        return `${category}_heading`
      })
    }, [lng])
    const changeCurrentTerm = useCallback(
      (category: string) => setCurrentTerm(`${category}_heading`),
      []
    )
    const refs = useMergeRefs(searchContainer, ref)

    return propertyCategoriesLoading ? (
      <Flex w="100%">
        <SearchLoader showDescription={showDescription} {...rest} flex={2.1} />
        {!isTabletOrMobile && <Box flex={1} />}
      </Flex>
    ) : (
      <>
        <FloatingButton icon={<FiSearch />} withArrow isOpen={isOpen} reference={searchContainer} />

        <SearchContainer
          ref={refs}
          position="relative"
          py={6}
          direction="column"
          justify={['flex-start', 'flex-start', 'center']}
          {...rest}
        >
          {showDescription && currentTerm && (
            <Heading as="h3" fontSize={['4xl', '6xl']} mb={6} fontWeight="extrabold">
              <Skeleton width="fit-content" isLoaded={!propertyCategoriesLoading}>
                {t(currentTerm)}
              </Skeleton>
            </Heading>
          )}
          <Tabs
            isFitted={isTabletOrMobile}
            colorScheme="orange"
            defaultIndex={
              localLastSelectedFilters?.tab?.id
                ? parseInt(localLastSelectedFilters?.tab?.id) - 1
                : 0
            }
          >
            <TabList borderBottomColor="transparent">
              {categories?.map((category) => (
                <Skeleton key={category?.id} isLoaded={!propertyCategoriesLoading}>
                  <CustomTooltip label={isResult ? t(`${category?.type}_tab_name`) : ''}>
                    <StyledTab
                      outlineColor="transparent"
                      px={0}
                      pt={0}
                      fontWeight="semibold"
                      pb={2}
                      borderBottomWidth={1}
                      mr={8}
                      _focus={{ borderLeft: 'none' }}
                      onFocus={() => changeCurrentTerm(category?.type as string)}
                    >
                      {/* <Center mr={isTabletOrMobile ? 1 : 2}>
                        {category?.icon_index && <Icon as={icons[category.icon_index - 1]} />}
                      </Center> */}
                      {t(`${category?.type}_tab_name`)}
                    </StyledTab>
                  </CustomTooltip>
                </Skeleton>
              ))}
            </TabList>
            <TabPanels>
              {categories?.map((category) => (
                <TabPanel key={category?.id} px={0}>
                  <SearchBar
                    initialValuesData={
                      localLastSelectedFilters && localLastSelectedFilters?.tab?.id === category?.id
                        ? localLastSelectedFilters
                        : {
                            propertyTypes: [],
                            minPrice: '',
                            maxPrice: '',
                            furnished: null,
                            search: '',
                            selectedArea: [],
                            bedRooms: '',
                            bathRooms: '',
                            rentalTerms: [],
                            propertyAvailabilities: ''
                          }
                    }
                    shouldShowSearch={shouldShowSearch}
                    isResult={isResult}
                    tab={category as PropertyCategory}
                    prices={
                      category?.type === Enum_Propertycategory_Type.Sale
                        ? isRand
                          ? generatePrices(60, 250000, 100000).arr
                          : generatePrices(60, 25000, 10000).arr
                        : isRand
                        ? generatePrices(70, 1000, 1000).arr
                        : generatePrices().arr
                    }
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </SearchContainer>
      </>
    )
  }
)
Search.defaultProps = {
  showDescription: false,
  shouldShowSearch: false
}
export default memo(Search)
